import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { JSX } from "react";
import {
  fetchAuthSession,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
} from "aws-amplify/auth";
import {
  apiGet,
  setActiveHouseholdId,
  setUnauthorizedHandler,
  friendlyError,
  ApiError,
} from "../../shared/http.js";
import { MeResponseSchema, type MeResponse } from "@recipevault/shared";
import { sync } from "../../sync/SyncService.js";
import { db, wipeAllTables } from "../../sync/db.js";

type Status = "loading" | "anonymous" | "authenticated";

interface AuthState {
  status: Status;
  me: MeResponse | null;
  error: string | null;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const v = useContext(AuthContext);
  if (!v) throw new Error("useAuth used outside AuthProvider");
  return v;
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<Status>("loading");
  const [me, setMe] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Read at fire time by the 401 handler below, which registers once (empty deps)
  // but needs the current status/re-entrancy state without re-subscribing.
  const statusRef = useRef(status);
  statusRef.current = status;
  const expiringRef = useRef(false);

  // Apply a MeResponse to state + side-effects. Shared by the network path and the
  // offline cached-fallback path so the active household + status stay consistent.
  function applyMe(parsed: MeResponse): void {
    setMe(parsed);
    const active =
      parsed.user.currentHouseholdId ?? parsed.memberships[0]?.householdId ?? null;
    setActiveHouseholdId(active);
    setStatus("authenticated");
  }

  async function loadMe(): Promise<void> {
    setError(null);
    const session = await fetchAuthSession();
    // No (or expired) Cognito tokens — genuinely signed out. Amplify serves cached
    // tokens offline, so reaching here means there's nothing to fall back to.
    if (!session.tokens?.accessToken) {
      setStatus("anonymous");
      setMe(null);
      setActiveHouseholdId(null);
      return;
    }
    try {
      const raw = await apiGet<unknown>("/me");
      const parsed = MeResponseSchema.parse(raw);
      // Cache the canonical row so an offline reload (PWA with full Dexie cache) can
      // stay authenticated without a network /me round-trip.
      await db.me.put({ ...parsed, _singleton: "me" });
      applyMe(parsed);
      // Refresh CDN cookies whenever the active household is (re-)set, which
      // covers both initial load and explicit household switches (auth.refresh()).
      void sync.refreshCdnCookies();
    } catch (e) {
      // A real 401 means the token was rejected — go anonymous. ANY other failure
      // (network TypeError, 5xx, parse error) with valid cached tokens + a cached
      // `me` row is treated as "offline": fall back to cache and stay authenticated
      // so the installed PWA is usable without a connection.
      const isAuthFailure = e instanceof ApiError && e.status === 401;
      if (!isAuthFailure) {
        const cached = await db.me.get("me").catch(() => undefined);
        if (cached) {
          applyMe({ user: cached.user, memberships: cached.memberships });
          void sync.refreshCdnCookies();
          return;
        }
      }
      setStatus("anonymous");
      setMe(null);
      setActiveHouseholdId(null);
      setError(friendlyError(e));
    }
  }

  useEffect(() => {
    void loadMe();
  }, []);

  // Mid-session token expiry: any authenticated request that comes back 401 signs
  // out, wipes Dexie, and hard-navigates to /login. Skips the anonymous bootstrap
  // 401 (loadMe handles that itself) and re-entrant fires while already expiring.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (statusRef.current !== "authenticated" || expiringRef.current) return;
      if (window.location.pathname === "/login") return;
      expiringRef.current = true;
      void (async () => {
        try {
          try {
            await amplifySignOut();
          } catch {
            /* session already dead */
          }
          await wipeAllTables();
        } finally {
          // Hard navigation must happen even if signOut/wipe threw (e.g. Dexie
          // quota/blocked) — otherwise the user is stranded on a dead-session page
          // with this handler permanently disabled. Also kills any in-flight sync
          // tick with the page.
          window.location.assign("/login");
        }
      })().catch(() => {
        // A wipe/quota rejection already triggered the redirect via `finally`;
        // swallow it here so it doesn't surface as an unhandled rejection.
      });
    });
    return () => {
      setUnauthorizedHandler(null);
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      status,
      me,
      error,
      async signIn(email, password) {
        await amplifySignIn({ username: email, password });
        await loadMe();
      },
      async signOut() {
        sync.stopCookieRefresh();
        // Server-side cookie revocation (DELETE /me/cdn-cookies). Best-effort — won't
        // block sign-out if it fails. Replaces the old client-side document.cookie
        // writes which were unreliable on HttpOnly cookies in some browsers.
        await sync.clearPhotoCookies();
        await amplifySignOut();
        // Wipe every Dexie table (not just `me`) so a second user signing in on the
        // same device/browser never sees the previous user's cached household data.
        await wipeAllTables().catch(() => undefined);
        setMe(null);
        setStatus("anonymous");
        setActiveHouseholdId(null);
      },
      refresh: loadMe,
    }),
    [status, me, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
